import React, { useState } from "react";
import { EyeMark } from "@/components/Ornaments";
import { PageHead } from "@/components/PageHead";
import { Seo } from "@/components/Seo";
import type { LanguageBinding } from "@/hooks/useLanguage";
import { cx } from "@/lib/classnames";

/**
 * Form endpoint.
 *
 * Uses FormSubmit.co. Two possible values:
 *
 *   (A) Plain-email endpoint — works immediately after the inbox owner
 *       confirms the one-time activation email, but the email address
 *       is visible in the bundled JS. Spam-scrapable.
 *
 *   (B) Hashed endpoint — available AFTER first activation. FormSubmit
 *       sends the inbox owner a one-off URL of the form
 *       `https://formsubmit.co/<random-hash>` which hides the address
 *       entirely. Swap FORM_ENDPOINT below to that URL and the email
 *       is no longer in the page source.
 *
 * Current setting is (A) until the activation email lands and the hash
 * can be pasted in place. See scripts/README-forms.md for the handover
 * steps when the hash arrives.
 */
const FORM_ENDPOINT = "https://formsubmit.co/ajax/b698aee8d9f1226f990db82a31024e0d";

/**
 * OPTIONAL archive endpoint — Google Apps Script Web App URL that appends
 * each submission as a row in a private Google Sheet. When set, submissions
 * are fire-and-forward-posted to this URL in parallel with the email
 * delivery above, so the operator can browse orders in a searchable/
 * sortable sheet instead of scrolling through inbox emails.
 *
 * The URL itself is safe to commit publicly — it's write-append only against
 * a single sheet in the owner's Drive. Rotate by redeploying Apps Script
 * and pasting the new URL here if abused. See scripts/apps-script-sheet-logger.gs
 * for the original setup.
 */
const SHEETS_ENDPOINT =
  "https://script.google.com/macros/s/AKfycbwGOHFEBIioz1qs1UvSlc1pvZtBv1GU9GP9AS8YhgdRrX-dho3XLWQnr1uI0uK_RFDU/exec";

// Default governorate per product requirement — most orders are Cairo-based.
const DEFAULT_GOVERNORATE_AR = "القاهرة";
const DEFAULT_GOVERNORATE_EN = "Cairo";

interface FormState {
  name: string;
  email: string;
  phone: string;
  address: string;
  governorate: string;
  qty: number;
  notes: string;
}

export const Contact: React.FC<{ language: LanguageBinding }> = ({ language }) => {
  const { t, lang, isRTL } = language;
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    phone: "",
    address: "",
    governorate: lang === "ar" ? DEFAULT_GOVERNORATE_AR : DEFAULT_GOVERNORATE_EN,
    qty: 1,
    notes: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const set =
    <K extends keyof FormState>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: key === "qty" ? Number(e.target.value) : e.target.value });

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    const payload = {
      _subject: `Book order — ${form.name}`,
      _template: "table",
      _captcha: "false",
      form_type: "book-order",
      language: lang,
      name: form.name,
      email: form.email,
      phone: form.phone,
      address: form.address,
      governorate: form.governorate,
      copies: form.qty,
      notes: form.notes,
    };
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Archive to the sheet in parallel. Fire-and-forget: a sheet failure
      // must NOT fail the user's submission (email delivery is the primary
      // source of truth).
      //
      // Content-Type is deliberately text/plain — Apps Script Web Apps do
      // not respond to CORS preflight OPTIONS requests. Using a 'simple'
      // content type keeps the POST single-hop; the Apps Script still
      // parses the body as JSON via `e.postData.contents`.
      if (SHEETS_ENDPOINT) {
        void fetch(SHEETS_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify(payload),
        }).catch(() => {});
      }
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }

  const inputCls = cx(
    "w-full bg-transparent px-4 py-3 outline-none text-sm",
    isRTL ? "ff-ar-sans" : "ff-en-body",
  );
  const inputStyle: React.CSSProperties = {
    color: "var(--ink)",
    border: "1px solid var(--hairline-light)",
    background: "var(--card-surface-warm)",
  };
  const labelCls = cx(
    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
    "text-[11px] mb-2 block",
  );

  return (
    <>
      <Seo
        title={t.meta.contact.title}
        description={t.meta.contact.description}
        lang={lang}
        path="/contact"
      />
      <PageHead
        kicker={t.contactPage.kicker}
        title={t.contactPage.title}
        tagline={t.contactPage.intro}
        isRTL={isRTL}
      />

      <section className="relative parchment-deckle">
        <div className="grain-soft" />
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-24 relative">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-7">
              {status === "sent" ? (
                <div
                  className="p-10 text-center"
                  style={{
                    border: "1px solid var(--hairline-light)",
                    background: "var(--card-surface-warm)",
                  }}
                >
                  <div className="flex justify-center mb-4" style={{ color: "var(--vermillion)" }}>
                    <EyeMark size={34} />
                  </div>
                  <p
                    className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif italic", "text-2xl")}
                    style={{ color: "var(--ink)" }}
                  >
                    {t.contactPage.thanks}
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid gap-5" noValidate>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls} style={{ color: "var(--muted-light)" }}>
                        {t.contactPage.name} *
                      </label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={set("name")}
                        required
                        minLength={2}
                        className={inputCls}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label className={labelCls} style={{ color: "var(--muted-light)" }}>
                        {t.contactPage.email} *
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        required
                        className={inputCls}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} style={{ color: "var(--muted-light)" }}>
                      {t.contactPage.address} *
                    </label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={set("address")}
                      required
                      minLength={5}
                      className={inputCls}
                      style={inputStyle}
                    />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-5">
                    <div className="sm:col-span-2">
                      <label className={labelCls} style={{ color: "var(--muted-light)" }}>
                        {t.contactPage.governorate} *
                      </label>
                      <input
                        type="text"
                        value={form.governorate}
                        onChange={set("governorate")}
                        required
                        minLength={2}
                        className={inputCls}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label className={labelCls} style={{ color: "var(--muted-light)" }}>
                        {t.contactPage.qty} *
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={form.qty}
                        onChange={set("qty")}
                        required
                        className={inputCls}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} style={{ color: "var(--muted-light)" }}>
                      {t.contactPage.phone}
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      className={inputCls}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className={labelCls} style={{ color: "var(--muted-light)" }}>
                      {t.contactPage.notes}
                    </label>
                    <textarea
                      rows={4}
                      value={form.notes}
                      onChange={set("notes")}
                      className={inputCls}
                      style={inputStyle}
                    />
                  </div>
                  {status === "error" && (
                    <div
                      className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "text-sm")}
                      style={{ color: "var(--vermillion)" }}
                    >
                      {t.contactPage.submitError}
                    </div>
                  )}
                  <div className="mt-2">
                    <button
                      type="submit"
                      disabled={status === "sending"}
                      className={cx(
                        isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                        "text-[13px] md:text-[14px] px-8 py-3.5 rounded-full transition-opacity",
                        status === "sending" && "opacity-60 cursor-not-allowed",
                      )}
                      style={{ background: "var(--vermillion)", color: "#fff3df" }}
                    >
                      {status === "sending" ? t.contactPage.sending : t.contactPage.send}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="md:col-span-5">
              <div
                className="p-8"
                style={{
                  border: "1px solid var(--hairline-light)",
                  background: "var(--card-surface-warm)",
                }}
              >
                <div
                  className={cx(
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest",
                    "text-[11px] mb-4",
                  )}
                  style={{ color: "var(--vermillion)" }}
                >
                  {t.contactPage.directTitle}
                </div>
                <p
                  className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "leading-relaxed")}
                  style={{ color: "var(--muted-light)" }}
                >
                  {t.contactPage.direct}
                </p>
                <div className="mt-6 space-y-3">
                  <a
                    href="https://www.facebook.com/profile.php?id=100064762146555"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 group"
                  >
                    <div
                      className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                      style={{ border: "1px solid var(--hairline-light)" }}
                    >
                      <svg width="10" height="14" viewBox="0 0 14 14" fill="var(--ink)">
                        <path d="M8 14V8h2l.3-2.3H8V4.2c0-.7.2-1.1 1.1-1.1h1.2V1c-.2 0-.9-.1-1.7-.1-1.7 0-2.9 1-2.9 3v1.8H3.8V8h1.9v6H8z" />
                      </svg>
                    </div>
                    <span
                      className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "text-base link-ul")}
                      style={{ color: "var(--ink)" }}
                    >
                      {t.contactPage.facebookLabel}
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
